import React, { useState } from 'react';

// FDI Two-Digit Tooth Numbering System
// Adults: Q1=11-18(upper-right), Q2=21-28(upper-left), Q3=31-38(lower-left), Q4=41-48(lower-right)
// Children: Q5=51-55(upper-right), Q6=61-65(upper-left), Q7=71-75(lower-left), Q8=81-85(lower-right)

const ADULT_TEETH = {
  upperRight: [18,17,16,15,14,13,12,11],
  upperLeft:  [21,22,23,24,25,26,27,28],
  lowerLeft:  [31,32,33,34,35,36,37,38],
  lowerRight: [48,47,46,45,44,43,42,41],
};
const CHILD_TEETH = {
  upperRight: [55,54,53,52,51],
  upperLeft:  [61,62,63,64,65],
  lowerLeft:  [71,72,73,74,75],
  lowerRight: [85,84,83,82,81],
};

const TOOTH_TYPES = {
  // Adults
  18:'molar',17:'molar',16:'molar',
  15:'premolar',14:'premolar',
  13:'canine',12:'incisor',11:'incisor',
  21:'incisor',22:'incisor',23:'canine',
  24:'premolar',25:'premolar',
  26:'molar',27:'molar',28:'molar',
  31:'incisor',32:'incisor',33:'canine',
  34:'premolar',35:'premolar',
  36:'molar',37:'molar',38:'molar',
  41:'incisor',42:'incisor',43:'canine',
  44:'premolar',45:'premolar',
  46:'molar',47:'molar',48:'molar',
  // Children (all simpler shapes)
  51:'incisor',52:'incisor',53:'canine',54:'molar',55:'molar',
  61:'incisor',62:'incisor',63:'canine',64:'molar',65:'molar',
  71:'incisor',72:'incisor',73:'canine',74:'molar',75:'molar',
  81:'incisor',82:'incisor',83:'canine',84:'molar',85:'molar',
};

// Simple SVG tooth shapes
function ToothShape({ type, size = 28, selected, highlighted }) {
  const w = size, h = size * 1.3;
  const fill   = selected    ? '#2563eb'
               : highlighted ? '#fbbf24'
               : '#e0e7ef';
  const stroke = selected    ? '#1d4ed8'
               : highlighted ? '#d97706'
               : '#94a3b8';

  if (type === 'molar') return (
    <svg width={w} height={h} viewBox="0 0 28 36">
      <rect x="3" y="8" width="22" height="22" rx="4" fill={fill} stroke={stroke} strokeWidth="2"/>
      <rect x="6" y="4" width="5" height="8" rx="2" fill={fill} stroke={stroke} strokeWidth="1.5"/>
      <rect x="17" y="4" width="5" height="8" rx="2" fill={fill} stroke={stroke} strokeWidth="1.5"/>
      <line x1="14" y1="10" x2="14" y2="28" stroke={stroke} strokeWidth="1" opacity="0.4"/>
      <line x1="5" y1="19" x2="23" y2="19" stroke={stroke} strokeWidth="1" opacity="0.4"/>
    </svg>
  );
  if (type === 'premolar') return (
    <svg width={w} height={h} viewBox="0 0 28 36">
      <ellipse cx="14" cy="23" rx="9" ry="11" fill={fill} stroke={stroke} strokeWidth="2"/>
      <rect x="10" y="4" width="4" height="12" rx="2" fill={fill} stroke={stroke} strokeWidth="1.5"/>
      <rect x="14" y="4" width="4" height="12" rx="2" fill={fill} stroke={stroke} strokeWidth="1.5"/>
    </svg>
  );
  if (type === 'canine') return (
    <svg width={w} height={h} viewBox="0 0 28 36">
      <ellipse cx="14" cy="24" rx="8" ry="10" fill={fill} stroke={stroke} strokeWidth="2"/>
      <path d="M14 4 Q16 12 14 18 Q12 12 14 4" fill={fill} stroke={stroke} strokeWidth="1.5"/>
    </svg>
  );
  // incisor
  return (
    <svg width={w} height={h} viewBox="0 0 28 36">
      <rect x="5" y="16" width="18" height="16" rx="4" fill={fill} stroke={stroke} strokeWidth="2"/>
      <rect x="9" y="4" width="10" height="14" rx="3" fill={fill} stroke={stroke} strokeWidth="1.5"/>
    </svg>
  );
}

export default function ToothChart({ selected = [], onChange, editable = true, compact = false }) {
  const [mode, setMode] = useState('adult'); // 'adult' | 'child'

  function toggle(num) {
    if (!editable) return;
    const numStr = String(num);
    if (selected.includes(numStr)) onChange(selected.filter(t => t !== numStr));
    else onChange([...selected, numStr]);
  }

  const toothSize = compact ? 22 : 32;
  const teeth = mode === 'adult' ? ADULT_TEETH : CHILD_TEETH;

  function QuadrantRow({ nums, label, position }) {
    return (
      <div style={{ display: 'flex', flexDirection: position === 'lower' ? 'column' : 'column-reverse', alignItems: 'center', gap: compact ? 2 : 6 }}>
        <div style={{ fontSize: compact ? 9 : 11, color: 'var(--color-ink-faint)', fontWeight: 600, marginBottom: position === 'upper' ? 0 : 2, marginTop: position === 'upper' ? 2 : 0 }}>
          {label}
        </div>
        <div style={{ display: 'flex', gap: compact ? 2 : 4, alignItems: 'flex-end' }}>
          {nums.map(n => {
            const sel = selected.includes(String(n));
            return (
              <div key={n}
                onClick={() => toggle(n)}
                title={`Tooth ${n} (${TOOTH_TYPES[n] || ''})`}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: editable ? 'pointer' : 'default', userSelect: 'none' }}>
                {position === 'lower' && (
                  <div style={{ fontSize: compact ? 8 : 10, fontWeight: sel ? 800 : 600, color: sel ? '#2563eb' : 'var(--color-ink-faint)', lineHeight: 1 }}>{n}</div>
                )}
                <ToothShape type={TOOTH_TYPES[n] || 'incisor'} size={toothSize} selected={sel} />
                {position === 'upper' && (
                  <div style={{ fontSize: compact ? 8 : 10, fontWeight: sel ? 800 : 600, color: sel ? '#2563eb' : 'var(--color-ink-faint)', lineHeight: 1 }}>{n}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const selCount = selected.length;

  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>
          🦷 FDI Tooth Chart
          {selCount > 0 && (
            <span style={{ background: '#2563eb', color: 'white', borderRadius: 100, padding: '1px 7px', fontSize: 11 }}>
              {selCount} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 0, border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', fontSize: 11 }}>
          {['adult','child'].map(m => (
            <button key={m} type="button" onClick={() => setMode(m)}
              style={{ padding: '3px 10px', border: 'none', cursor: 'pointer', fontWeight: 700, background: mode === m ? 'var(--color-primary)' : 'transparent', color: mode === m ? 'white' : 'var(--color-ink-soft)', transition: 'all 0.12s', textTransform: 'capitalize' }}>
              {m === 'adult' ? 'Adult' : 'Child'}
            </button>
          ))}
        </div>
      </div>

      {/* Selected chips */}
      {selCount > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {selected.map(t => (
            <span key={t} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: '#dbeafe', color: '#2563eb', border: '1px solid #93c5fd', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Tooth {t}
              {editable && (
                <button type="button" onClick={() => toggle(Number(t))}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#2563eb', padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
              )}
            </span>
          ))}
          {editable && (
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, border: '1px dashed var(--color-border)', background: 'transparent', color: 'var(--color-ink-faint)', cursor: 'pointer' }}>
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Chart */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        {/* Upper jaw */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 4 }}>
          <QuadrantRow nums={teeth.upperRight} label="Upper Right" position="upper" />
          <div style={{ width: 1, background: 'var(--color-border)', alignSelf: 'stretch', margin: '0 4px' }} />
          <QuadrantRow nums={teeth.upperLeft}  label="Upper Left"  position="upper" />
        </div>

        {/* Midline */}
        <div style={{ width: '100%', borderTop: '2px dashed var(--color-border)', marginBottom: 4 }} />

        {/* Lower jaw */}
        <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
          <QuadrantRow nums={teeth.lowerRight} label="Lower Right" position="lower" />
          <div style={{ width: 1, background: 'var(--color-border)', alignSelf: 'stretch', margin: '0 4px' }} />
          <QuadrantRow nums={teeth.lowerLeft}  label="Lower Left"  position="lower" />
        </div>
      </div>

      {editable && (
        <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', marginTop: 10, textAlign: 'center' }}>
          Click teeth to select/deselect · FDI two-digit numbering system
        </div>
      )}
    </div>
  );
}
